/**
 * FontPreloader: Optimized font loading strategy
 * - Caches Titillium Web locally (IndexedDB + localStorage)
 * - Prevents cold start font loading delays
 * - Falls back to system fonts immediately
 * - Async font warming with requestIdleCallback
 */

import { supabase } from '@/lib/supabase/client';

const FONT_CACHE_KEY = 'font-cache-titillium-web';
const FONT_CACHE_VERSION = 'v1';
const FONT_CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CachedFont {
  version: string;
  timestamp: number;
  fontFamily: string;
  cssText: string;
}

/**
 * Apply Titillium Web font immediately using CSS variables
 * This is synchronous and non-blocking
 */
export function applyTitilliumWebFast() {
  if (typeof document === 'undefined') return;
  
  try {
    const root = document.documentElement;
    // Set CSS variable immediately - non-blocking
    const fontStack = "'Titillium Web', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
    root.style.setProperty('--font-sans', fontStack);
    
    // Apply to body with Titillium Web first
    document.body.style.fontFamily = fontStack;
    
    // Return early - real Titillium Web will load async
  } catch (e) {
    // swallow - fallback fonts already applied
  }
}

/**
 * Load Titillium Web from Google Fonts or local cache
 * This runs async and doesn't block initial render
 */
export async function preloadTitilliumWeb() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  
  try {
    // Check if already in document
    if (document.querySelector('link[href*="Titillium+Web"]')) return;
    if (document.querySelector(`style[data-font="titillium"]`)) return;
    
    // Try to load from cache first
    const cached = getCachedFont();
    if (cached) {
      applyFontFromCache(cached);
      return;
    }
    
    // Load from Google Fonts
    const fontUrl = 'https://fonts.googleapis.com/css2?family=Titillium+Web:wght@400;600;700&display=swap';
    
    // Use fetch with abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
    
    try {
      const response = await fetch(fontUrl, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) throw new Error('Font fetch failed');
      
      const cssText = await response.text();
      
      // Cache for future use
      cacheFontLocally(cssText);
      
      // Apply font
      injectFontCSS(cssText);
      
      // Pre-warm font rendering on idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => warmFontRendering(), { timeout: 2000 });
      } else {
        // Fallback: schedule for next idle
        setTimeout(warmFontRendering, 100);
      }
    } catch (e) {
      clearTimeout(timeoutId);
      // Network error or timeout - use fallback
      // Titillium Web will load in background later
    }
  } catch (e) {
    // swallow - system fonts already applied
  }
}

/**
 * Warm font rendering to trigger font loading
 * Creates invisible text to force browser to load the font
 */
function warmFontRendering() {
  if (typeof document === 'undefined') return;
  
  try {
    const div = document.createElement('div');
    div.style.fontFamily = "'Titillium Web', system-ui";
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'nowrap';
    div.textContent = 'Titillium Web Loading';
    document.body.appendChild(div);
    
    // Trigger layout - forces font loading
    void div.offsetWidth;
    
    // Clean up
    setTimeout(() => div.remove(), 100);
  } catch (e) {
    // swallow
  }
}

/**
 * Inject font CSS into document
 */
function injectFontCSS(cssText: string) {
  if (typeof document === 'undefined') return;
  
  try {
    const style = document.createElement('style');
    style.setAttribute('data-font', 'titillium');
    style.textContent = cssText;
    document.head.appendChild(style);
  } catch (e) {
    // swallow
  }
}

/**
 * Cache font CSS locally
 */
function cacheFontLocally(cssText: string) {
  try {
    const cacheData: CachedFont = {
      version: FONT_CACHE_VERSION,
      timestamp: Date.now(),
      fontFamily: 'Titillium Web',
      cssText
    };
    
    // Try IndexedDB first
    if ('indexedDB' in window) {
      try {
        const request = indexedDB.open('FontCache', 1);
        request.onsuccess = () => {
          const db = request.result;
          const tx = db.transaction('fonts', 'readwrite');
          tx.objectStore('fonts').put(cacheData, FONT_CACHE_KEY);
        };
        request.onupgradeneeded = (e: any) => {
          const db = e.target.result;
          if (!db.objectStoreNames.contains('fonts')) {
            db.createObjectStore('fonts');
          }
        };
      } catch (e) {
        // fallback to localStorage
        cacheToLocalStorage(cacheData);
      }
    } else {
      // fallback to localStorage
      cacheToLocalStorage(cacheData);
    }
  } catch (e) {
    // swallow
  }
}

/**
 * Cache font data in localStorage
 */
function cacheToLocalStorage(cacheData: CachedFont) {
  try {
    localStorage.setItem(FONT_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    // swallow - quota exceeded or disabled
  }
}

/**
 * Get cached font from IndexedDB or localStorage
 */
function getCachedFont(): CachedFont | null {
  try {
    // Try localStorage first (faster, synchronous)
    const cached = localStorage.getItem(FONT_CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached) as CachedFont;
      
      // Check expiry
      if (Date.now() - data.timestamp < FONT_CACHE_EXPIRY) {
        return data;
      } else {
        // Expired - clear
        localStorage.removeItem(FONT_CACHE_KEY);
      }
    }
  } catch (e) {
    // swallow
  }
  
  return null;
}

/**
 * Apply font from cache
 */
function applyFontFromCache(cached: CachedFont) {
  try {
    injectFontCSS(cached.cssText);
  } catch (e) {
    // swallow
  }
}

/**
 * Initialize font loading (call once on app load)
 * Non-blocking, returns immediately
 */
export function initFontPreloading() {
  if (typeof window === 'undefined') return;
  
  try {
    // Apply system fonts immediately
    applyTitilliumWebFast();
    
    // Load real font async (don't await)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => preloadTitilliumWeb(), { timeout: 2000 });
    } else {
      // Fallback: schedule async
      setTimeout(() => preloadTitilliumWeb(), 0);
    }
  } catch (e) {
    // swallow
  }
}

/**
 * Pre-warm Supabase connection (idle callback)
 * Establishes connection without blocking initial render
 */
export function preWarmSupabaseConnection() {
  if (typeof window === 'undefined') return;
  
  try {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        // Trigger a lightweight query to warm up the connection
        void supabase.auth.getSession();
      }, { timeout: 3000 });
    }
  } catch (e) {
    // swallow
  }
}
